// The simulated Windows endpoint behind each Support Desk ticket. Shared by the
// Support Desk UI and by the guest-progress claim (which replays a guest's
// commands on a fresh endpoint instead of trusting stored output).
import { WinShell, renew } from '../../engine/pc/winshell.js';
import { hostMediaUp } from '../../engine/net/sim.js';
import { branch, twoLan } from '../d01/topologies.js';
import { PRACTICE_TICKET } from './tickets.js';

export function endpointNetwork(ticket) {
  const net = ticket.topology === 'scope' ? twoLan() : branch();
  const pc = net.devices.PC1;
  pc.hostname = 'NS-' + ticket.topology.toUpperCase() + '-WS01';
  if (ticket.topology === 'physical') {
    const cable = net.links.find((l) => l.a.dev === 'PC1' || l.b.dev === 'PC1');
    if (cable) cable.cable.seated = false;
  }
  if (ticket.topology === 'dhcp') {
    pc.ipcfg = { dhcp: true, ip: null, mask: null, gw: null, dns: ['8.8.8.8'], ipv6: [] };
    renew(net, pc);
  }
  if (ticket.topology === 'lease') {
    // Ticket 1: the desk's gateway runs a working DHCP pool, but the laptop arrived from
    // another desk with a stale lease that Windows has dropped — no IPv4 address, no gateway.
    // Nothing is printed by hand: ipconfig /renew asks this pool for a real lease.
    const r1 = net.devices.R1;
    r1.dhcp.excluded.push(['192.168.10.1', '192.168.10.49']);
    r1.dhcp.pools['DESK-LAN'] = { name: 'DESK-LAN', network: '192.168.10.0', mask: '255.255.255.0', router: '192.168.10.1', dns: ['8.8.8.8'] };
    pc.ipcfg = { dhcp: true, ip: null, mask: null, gw: null, dns: ['8.8.8.8'], ipv6: [] };
    pc.lease = null; pc.apipa = null; pc.arp = {};
  }
  return net;
}

export const endpointShell = (ticket) => new WinShell(endpointNetwork(ticket), 'PC1');

/** Ports on the practice laptop the loose cable can be offered to. Only the RJ-45 port takes it. */
export const PRACTICE_PORTS = [
  { id: 'ethernet', label: 'Ethernet port (RJ-45)', valid: true },
  { id: 'usb', label: 'USB-C port', valid: false, why: 'That is a USB-C port. An Ethernet (RJ-45) plug only fits the Ethernet port.' },
  { id: 'hdmi', label: 'HDMI port', valid: false, why: 'That is the HDMI (display) port. Look for the wider, square Ethernet port.' },
];

/**
 * Offer the practice cable's loose plug to a port. Only the Ethernet port seats the cable;
 * anything else leaves the simulator unchanged.
 * @returns {{ ok: boolean, connected: boolean, message: string }}
 */
export function connectPracticeCable(shell, portId) {
  const port = PRACTICE_PORTS.find((p) => p.id === portId);
  if (!port?.valid) {
    return { ok: false, connected: hostMediaUp(shell.net, shell.d), message: port?.why || 'Not connected. Drop the plug on the laptop’s Ethernet port.' };
  }
  const connected = setEndpointCableConnected(shell, true);
  return { ok: connected, connected, message: connected ? 'Cable connected to the Ethernet port. The link is up.' : 'The plug is in, but the link is still down.' };
}

/** Change the practice endpoint's cable state and report the resulting link state. */
export function setEndpointCableConnected(shell, connected) {
  const link = shell?.net?.links?.find((l) => l.a.dev === shell.id || l.b.dev === shell.id);
  if (!link?.cable) return false;
  link.cable.seated = !!connected;
  return hostMediaUp(shell.net, shell.d);
}

/** Replay the optional practice ticket's recorded actions on a fresh simulated endpoint. */
export function replayPracticeEvents(events = []) {
  const shell = endpointShell(PRACTICE_TICKET);
  const evidence = [];
  const transcript = [];
  const actions = Array.isArray(events) ? events.slice(0, 40) : [];
  for (let eventIndex = 0; eventIndex < actions.length; eventIndex++) {
    const action = actions[eventIndex];
    if (action?.type === 'reconnect') {
      // recorded only after a valid cable-to-Ethernet-port connection in the UI
      const { connected } = connectPracticeCable(shell, 'ethernet');
      transcript.push({ type: 'action', command: 'Cable plugged into the laptop’s Ethernet port', output: `Ethernet link ${connected ? 'connected' : 'disconnected'}.`, connected, eventIndex });
      continue;
    }
    if (action?.type !== 'command' || typeof action.command !== 'string' || action.command.length > 120) continue;
    const connected = hostMediaUp(shell.net, shell.d);
    const result = shell.exec(action.command);
    const output = result.lines.map((line) => line.text).join('\n');
    evidence.push({ command: action.command, output, connected, eventIndex });
    transcript.push({ type: 'command', command: action.command, output, connected, eventIndex });
  }
  return { shell, evidence, transcript, connected: hostMediaUp(shell.net, shell.d) };
}

/** Run commands, in order, on a fresh endpoint for this ticket and return the real output. */
export function replayEvidence(ticket, commands) {
  const shell = endpointShell(ticket);
  return commands.map((command) => ({ command, output: shell.exec(command).lines.map((x) => x.text).join('\n') }));
}
